// ============================================================================
// Ant Design theme — Choice MCN Platform
// File: src/theme/antdTheme.js
//
// Single source of truth for brand colors, spacing, and typography.
// Used by ConfigProvider in App.js to theme ALL antd components consistently.
// ============================================================================

export const brandColors = {
  // Primary brand
  primary: '#6366f1',          // Indigo — matches the existing purple header
  primaryHover: '#4f46e5',
  primaryActive: '#4338ca',

  // Semantic
  success: '#10b981',          // Emerald
  warning: '#f59e0b',          // Amber
  error: '#ef4444',            // Red
  info: '#06b6d4',             // Cyan

  // Neutrals
  bgLayout: '#f5f6fa',         // Body background
  bgContainer: '#ffffff',      // Cards, panels
  bgElevated: '#ffffff',
  bgSpotlight: '#fafbff',

  textPrimary: '#0f172a',
  textSecondary: '#475569',
  textTertiary: '#94a3b8',
  textDisabled: '#cbd5e1',

  borderColor: '#e2e8f0',
  borderColorSplit: '#f1f5f9',
};

const antdTheme = {
  token: {
    colorPrimary: brandColors.primary,
    colorSuccess: brandColors.success,
    colorWarning: brandColors.warning,
    colorError: brandColors.error,
    colorInfo: brandColors.info,

    colorBgLayout: brandColors.bgLayout,
    colorBgContainer: brandColors.bgContainer,
    colorBgElevated: brandColors.bgElevated,
    colorBgSpotlight: brandColors.bgSpotlight,

    colorText: brandColors.textPrimary,
    colorTextSecondary: brandColors.textSecondary,
    colorTextTertiary: brandColors.textTertiary,
    colorTextDisabled: brandColors.textDisabled,

    colorBorder: brandColors.borderColor,
    colorBorderSecondary: brandColors.borderColorSplit,

    borderRadius: 8,
    borderRadiusLG: 12,
    borderRadiusSM: 6,

    // Typography
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontSize: 14,
    fontSizeHeading1: 28,
    fontSizeHeading2: 22,
    fontSizeHeading3: 18,
    fontSizeHeading4: 16,
    fontSizeHeading5: 14,

    // Spacing
    sizeStep: 4,
    sizeUnit: 4,
    marginXS: 8,
    marginSM: 12,
    margin: 16,
    marginMD: 20,
    marginLG: 24,
    marginXL: 32,
    paddingXS: 8,
    paddingSM: 12,
    padding: 16,
    paddingLG: 24,

    // Motion
    motionDurationFast: '0.1s',
    motionDurationMid: '0.2s',
    motionDurationSlow: '0.3s',

    // Shadows
    boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06), 0 1px 2px rgba(15, 23, 42, 0.04)',
    boxShadowSecondary: '0 4px 12px rgba(15, 23, 42, 0.08)',
    boxShadowTertiary: '0 6px 24px rgba(15, 23, 42, 0.08)',
  },
  components: {
    Layout: {
      headerBg: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)',
      headerColor: '#ffffff',
      siderBg: '#ffffff',
      bodyBg: brandColors.bgLayout,
    },
    Table: {
      headerBg: '#f8fafc',
      headerColor: brandColors.textSecondary,
      headerSplitColor: brandColors.borderColorSplit,
      borderColor: brandColors.borderColorSplit,
      rowHoverBg: '#f8fafc',
      cellPaddingBlock: 12,
      cellPaddingInline: 16,
    },
    Card: {
      paddingLG: 20,
      headerBg: 'transparent',
    },
    Statistic: {
      titleFontSize: 12,
      contentFontSize: 24,
    },
    Tabs: {
      cardBg: 'transparent',
      itemColor: brandColors.textSecondary,
      itemSelectedColor: brandColors.primary,
      itemHoverColor: brandColors.primary,
      inkBarColor: brandColors.primary,
    },
    Button: {
      controlHeight: 36,
      controlHeightLG: 44,
      controlHeightSM: 28,
      fontWeight: 500,
    },
    Tag: {
      defaultBg: '#f1f5f9',
      defaultColor: brandColors.textSecondary,
    },
  },
};

export default antdTheme;
